import { useState, type ReactNode } from 'react';
import { useAuth } from '@/hooks/use-auth';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { ErrorBoundary } from './ErrorBoundary';

interface AppShellProps {
  children: ReactNode;
}

/**
 * The persistent application shell.
 *
 * Layout:
 * ┌──────────┬─────────────────────────────────┐
 * │          │  Header (breadcrumb + user)      │
 * │ Sidebar  ├─────────────────────────────────┤
 * │          │  Page content (scrollable)       │
 * └──────────┴─────────────────────────────────┘
 *
 * The sidebar collapses to an icon rail when toggled.
 * On narrow viewports (<768 px) the sidebar becomes a drawer
 * triggered by the hamburger button in the header.
 */
export function AppShell({ children }: AppShellProps) {
  const { session } = useAuth();
  const [collapsed, setCollapsed] = useState(false);

  const permissions = session?.permissions ?? [];

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      {/* Sidebar */}
      <Sidebar
        permissions={permissions}
        collapsed={collapsed}
        onToggle={() => setCollapsed((c) => !c)}
      />

      {/* Main column: header + scrollable content */}
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        <Header />

        {/* Page content */}
        <main className="flex-1 overflow-y-auto">
          <ErrorBoundary>
            {children}
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
}
